// EXPERIMENT: constituent_psf_experiment (remove with experiment cleanup)
import type { ComponentProps } from 'react';
import { useMemo } from 'react';

import type { SocialSharing } from '@change/ds-core-ui/SocialSharing';
import { EmailOutlineIcon } from '@change/ds-icons/EmailOutlineIcon';
import { FacebookIcon } from '@change/ds-icons/FacebookIcon';
import { FacebookMessengerIcon } from '@change/ds-icons/FacebookMessengerIcon';
import { InstagramIcon } from '@change/ds-icons/InstagramIcon';
import { LineIcon } from '@change/ds-icons/LineIcon';
import { LinkIcon } from '@change/ds-icons/LinkIcon';
import { NextDoorIcon } from '@change/ds-icons/NextDoorIcon';
import { RedditIcon } from '@change/ds-icons/RedditIcon';
import { ShareIcon } from '@change/ds-icons/ShareIcon';
import { TikTokIcon } from '@change/ds-icons/TikTokIcon';
import { TwitterIcon } from '@change/ds-icons/TwitterIcon';
import { VkIcon } from '@change/ds-icons/VkIcon';
import { WhatsAppIcon } from '@change/ds-icons/WhatsAppIcon';

import type { useShareActions } from '#src/app/pages/psf/promoteOrShare/shared/hooks';
import type { LocalShareAction } from '#src/app/shared/components/localSignerShare';
import type { ShareActionComponentProps, ShareTrackingDataOverride } from '#src/app/shared/hooks/share';
import { useDeviceMessagingIcon } from '#src/app/shared/hooks/share';

type ButtonConfig = ReturnType<typeof useShareActions>['data']['buttonConfigs'][number];

type ChannelIcon = Readonly<{
	icon: React.ReactElement;
	variant: ComponentProps<typeof SocialSharing>['variant'];
}>;

// The icon each channel shows in the current sheet's grid (`RoundShareIconButton`). SMS is resolved per
// device below, as the grid does.
const ICON_BY_CHANNEL: Readonly<Record<Exclude<ButtonConfig['channel'], 'sms'>, ChannelIcon>> = {
	facebook: { icon: <FacebookIcon />, variant: 'facebook' },
	facebookMessenger: { icon: <FacebookMessengerIcon />, variant: 'facebookMessenger' },
	twitter: { icon: <TwitterIcon />, variant: 'twitter' },
	whatsapp: { icon: <WhatsAppIcon />, variant: 'whatsapp' },
	instagram: { icon: <InstagramIcon />, variant: 'instagram' },
	instagramStories: { icon: <InstagramIcon />, variant: 'instagram' },
	nextdoor: { icon: <NextDoorIcon />, variant: 'nextdoor' },
	reddit: { icon: <RedditIcon />, variant: 'reddit' },
	tiktok: { icon: <TikTokIcon />, variant: 'tiktok' },
	vk: { icon: <VkIcon />, variant: 'default' },
	line: { icon: <LineIcon />, variant: 'default' },
	email: { icon: <EmailOutlineIcon />, variant: 'default' },
	copy: { icon: <LinkIcon />, variant: 'default' },
	mobileNativeShare: { icon: <ShareIcon />, variant: 'default' },
};

// The current sheet's `share_placement` values (see `petitionShareSlideUp/constants`): Copy link is its
// primary action button, the other channels sit in its grid.
const ACTION_BUTTON_TRACKING_DATA = { share_placement: 'action_button' } as const;
const CHANNEL_GRID_TRACKING_DATA = { share_placement: 'channel_grid' } as const;

/**
 * Wraps a channel's `ShareAction` with the sheet's behavior, which the presentational local-signer
 * screen does not know about: the `share_placement` the sheet's own buttons send, and closing the sheet
 * before a social share (as the channel grid does), so an education modal never stacks on top of it.
 */
function withSheetBehavior(
	ShareAction: ButtonConfig['ShareAction'],
	{ trackingData, onBeforeShare }: Readonly<{ trackingData: ShareTrackingDataOverride; onBeforeShare?: () => void }>,
): ButtonConfig['ShareAction'] {
	const SheetShareAction = ({
		trackingData: callerTrackingData,
		onBeforeAction,
		...props
	}: ShareActionComponentProps) => (
		<ShareAction
			{...props}
			trackingData={{ ...trackingData, ...callerTrackingData }}
			onBeforeAction={async () => {
				onBeforeShare?.();
				await onBeforeAction?.();
			}}
		/>
	);
	return SheetShareAction;
}

/**
 * Maps the combo page's share configs (server-provided channels, the page's share tracking, and the
 * Facebook post sheet interception) to the local-signer share screen's shape, adding the icons the
 * channel grid draws for each channel.
 */
export function useLocalSignerShareActions(
	buttonConfigs: readonly ButtonConfig[],
	close: () => void,
): readonly LocalShareAction[] {
	const { MessagingIconComponent, variant: smsVariant } = useDeviceMessagingIcon();

	return useMemo(
		() =>
			buttonConfigs.map(({ ShareAction, channel, label, successLabel, url }) => {
				const { icon, variant } =
					channel === 'sms' ? { icon: <MessagingIconComponent />, variant: smsVariant } : ICON_BY_CHANNEL[channel];
				const isCopy = channel === 'copy';
				return {
					// Copy link is the screen's primary button and keeps the sheet open for its inline "copied" state.
					ShareAction: withSheetBehavior(ShareAction, {
						trackingData: isCopy ? ACTION_BUTTON_TRACKING_DATA : CHANNEL_GRID_TRACKING_DATA,
						onBeforeShare: isCopy ? undefined : close,
					}),
					channel,
					icon,
					variant,
					label,
					successLabel,
					url,
				};
			}),
		[buttonConfigs, close, MessagingIconComponent, smsVariant],
	);
}
